<template>
	<div class="customer-uploads-page">
		<!-- Page Header -->
		<div class="page-header">
			<h1>Customer Uploads</h1>
			<p class="subtitle">Showing {{ documents.length }} recent document(s)</p>
		</div>

		<!-- Loading Overlay -->
		<div v-if="loading" class="loading-overlay">
			<div class="spinner"></div>
			<p>Loading documents...</p>
		</div>

		<!-- Documents Table -->
		<div v-else class="documents-section">
			<table v-if="documents && documents.length > 0" class="simple-table">
				<thead>
					<tr>
						<th v-for="col in documentColumns" :key="col.key">{{ col.label }}</th>
						<th>Doc</th>
						<th>Details</th>
					</tr>
				</thead>
				<tbody>
					<tr v-for="(doc, idx) in documents" :key="idx">
						<td v-for="col in documentColumns" :key="col.key">
							{{ col.formatter ? col.formatter(doc) : (doc[col.key] || '-') }}
						</td>
						<td>
							<a v-if="doc.url" :href="doc.url" target="_blank" class="folder-link" title="Open document">📁</a>
							<span v-else>-</span>
						</td>
						<td>
							<button class="btn-expand" @click="openDocumentModal(doc)" title="Show details">+</button>
						</td>
					</tr>
				</tbody>
			</table>

			<div v-else class="empty-state">
				No documents found
			</div>
		</div>

		<!-- Document Detail Modal -->
		<MGDialog
			v-model="showDocumentModal"
			title="Document Details"
			max-width="1200px"
			contentHeight="700px"
		>
			<template #content>
				<div class="modal-content" v-if="selectedDocument">
					<div class="modal-image-container">
					<img class="modal-image" :src="selectedDocument.url" />
				</div>
				<pre class="modal-json">{{ JSON.stringify(selectedDocument, null, 2) }}</pre>
				</div>
			</template>
			<template #actions>
				<button class="action-btn secondary" @click="showDocumentModal = false">Close</button>
			</template>
		</MGDialog>
	</div>
</template>

<script>
import AdminServices from '@/admin/mixins/AdminServices';
import MGDialog from '@/components/elements/MGDialog.vue';

export default {
	name: 'CustomerUploadsPage',
	mixins: [AdminServices],
	components: {
		MGDialog
	},
	data() {
		return {
			documents: [],
			customers: [],
			loading: true,
			reprocessingId: null,
			showDocumentModal: false,
			selectedDocument: null,
			documentColumns: [
				{
					key: 'createDateTime',
					label: 'Added',
					formatter: (doc) => this.formatDateTime(doc.createDateTime)
				},
				{ key: 'email', label: 'Customer' },
				{
					key: 'processedDateTime',
					label: 'Processed',
					formatter: (doc) => this.formatProcessedTime(doc.processedDateTime)
				}
			]
		};
	},
	methods: {
		formatDateTime(dateString) {
			if (!dateString) return '-';
			const date = new Date(dateString);
			return date.toLocaleString('en-GB', {
				year: 'numeric',
				month: '2-digit',
				day: '2-digit',
				hour: '2-digit',
				minute: '2-digit'
			});
		},
		formatProcessedTime(dateString) {
			if (!dateString) return '-';
			const date = new Date(dateString);
			return date.toLocaleTimeString('en-GB', {
				hour: '2-digit',
				minute: '2-digit'
			});
		},
		canRetry(document) {
			// Can retry if there's a processed datetime (document failed processing)
			return !!document.processedDateTime;
		},
		retryProcessing(documentId) {
			this.reprocessingId = documentId;
			this._retryDocument(documentId, null, () => {
				this.showToast('Document reprocessing initiated');
				this.reprocessingId = null;
				// Refresh the documents list
				this.loadDocuments();
			}, (error) => {
				this.showError(error);
				this.reprocessingId = null;
			});
		},
		loadDocuments() {
			this.loading = true;
			// Fetch customers first
			this._getCustomers((customersData) => {
				this.customers = customersData || [];
				// Then fetch documents
				this._getDocuments((documentsData) => {
					this.documents = documentsData || [];
					// Join documents with customer emails
					this.documents.forEach(doc => {
						const customer = this.customers.find(c => c.id === doc.customerId);
						if (customer) {
							doc.email = customer.person.email;
						}
					});
					this.loading = false;
				});
			});
		},
		openDocumentModal(document) {
			this.selectedDocument = document;
			this.showDocumentModal = true;
		},
		showToast(message) {
			// This would integrate with your toast notification system
			console.log('Toast:', message);
		},
		showError(message) {
			// This would integrate with your error notification system
			console.error('Error:', message);
		}
	},
	mounted() {
		this.loadDocuments();
	}
};
</script>

<style scoped>
.customer-uploads-page {
	min-height: 100%;
	padding: 20px;
}

.documents-section {
	background-color: var(--admin-bg-secondary);
	border-radius: var(--border-radius-m);
	padding: 20px;
	box-shadow: var(--box-shadow);
	overflow-x: auto;
}

.simple-table {
	width: 100%;
	border-collapse: collapse;
	font-size: 13px;
}

.simple-table thead {
	background-color: var(--admin-hover-bg);
}

.simple-table th {
	padding: 12px;
	text-align: left;
	color: var(--admin-text-primary);
	font-weight: 600;
	border-bottom: 1px solid var(--admin-border);
}

.simple-table td {
	padding: 12px;
	color: var(--admin-text-primary);
	border-bottom: 1px solid var(--admin-border);
}

.simple-table tbody tr:hover {
	background-color: var(--admin-hover-bg);
}

.folder-link {
	font-size: 18px;
	cursor: pointer;
	text-decoration: none;
	transition: transform 0.2s ease;
	display: inline-block;
}

.folder-link:hover {
	transform: scale(1.2);
}

.modal-content {
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 20px;
	align-items: start;
}

.modal-image-container {
	display: flex;
	align-items: flex-start;
	justify-content: center;
	max-height: 600px;
	overflow-y: auto;
	border-radius: var(--border-radius-s);
	border: 1px solid var(--admin-border);
	padding: 10px;
}

.modal-image {
	max-width: 100%;
	object-fit: contain;
}

.modal-json {
	background-color: var(--admin-hover-bg);
	padding: 12px;
	border-radius: var(--border-radius-s);
	border: 1px solid var(--admin-border);
	color: var(--admin-text-primary);
	font-family: 'Courier New', monospace;
	font-size: 11px;
	max-height: 600px;
	overflow-y: auto;
	white-space: pre-wrap;
	word-wrap: break-word;
	line-height: 1.4;
	margin: 0;
}

/* Responsive */
@media (max-width: 768px) {
	.customer-uploads-page {
		padding: 10px;
	}

	.page-header h1 {
		font-size: 20px;
	}

	.documents-section {
		padding: 15px;
	}
}
</style>
